// Single source of truth for "should this practice see Follow-Up V2?".
//
// Reads PracticePreferences.is_followup_v2_enabled (BE PR #5536). When true,
// the FE routes every "Follow up" entry point to FollowUpV2 and (for regular
// users) hides the V1 Dashboard nav entry. When false, everything stays on
// V1.
//
// Super-admins are NOT special-cased here — that exemption lives where it
// belongs: the sidebar nav (NavConfig.jsx) shows BOTH Dashboard entries to
// super-admins regardless of the flag so they can compare V1/V2. The
// follow-up modal itself still follows the practice flag — if a super-admin
// wants to see V2 on a non-V2 practice they flip the flag in the Feature
// Flags admin.
//
// Why one hook instead of 6 inline selector reads: every callsite was
// duplicating the same `state.practice?.practice?.preferences?.…` selector.
// Collapsing to one signal — the per-practice flag — makes the routing
// predictable and removes boilerplate per consumer.

import { useSelector } from '../redux/store';

export default function useIsFollowUpV2Practice(contextPracticeId) {
    const hasExplicitContext = contextPracticeId !== undefined;

    return useSelector((state) => {
        const loaded = state.practice?.practice;
        const prefs = loaded?.preferences;
        // Return null while practice preferences haven't resolved yet (initial state
        // has practice:{} with no preferences key). Callers that need to skip navigation
        // decisions until state is known check for null explicitly; all other callers
        // treat null as falsy (same as false) — harmless during the brief loading window.
        if (prefs == null) return null;
        // STALE-PRACTICE GUARD: state.practice.practice lags after a practice switch —
        // it still holds the PREVIOUS practice until getPracticeItem(newId) resolves.
        // Without this guard, switching from a V2 practice to a non-V2 one briefly
        // reports V2=true for the new practice, so ContactLeadPopupRouter mounts
        // FollowUpV2 on a non-V2 lead (AttendedFamilyHero gets the wrong shape →
        // blank table on close). Only trust the flag when the loaded practice IS the
        // active one; otherwise return null (loading) → safe V1 default until it resolves.
        if (hasExplicitContext && contextPracticeId == null) return null;
        const activeId = hasExplicitContext
            ? contextPracticeId
            : state.practiceTreatmentList?.practiceId;
        if (activeId != null && loaded?.id != null && String(loaded.id) !== String(activeId)) {
            return null;
        }
        return !!prefs.is_followup_v2_enabled;
    });
}
